import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';

import { InteractionsService } from './interactions.service';
import { UserPreferences, UsersService, DEFAULT_USER_PREFERENCES } from './users.service';
import { TenantService } from './tenant.service';

export interface TutorialStep {
  id: 'archives' | 'interactions' | 'results' | 'references' | 'models' | 'retrievers' | 'retriever-results' | 'settings';
  route: string;
  titleKey: string;
  contentKey: string;
  hintKey: string;
  gifPath: string;
}

export interface TutorialState {
  isOpen: boolean;
  currentStepIndex: number;
  steps: TutorialStep[];
}

@Injectable({ providedIn: 'root' })
export class TutorialService {
  private readonly steps: TutorialStep[] = [
    {
      id: 'archives',
      route: '/archives',
      titleKey: 'TUTORIAL.STEPS.ARCHIVES.TITLE',
      contentKey: 'TUTORIAL.STEPS.ARCHIVES.CONTENT',
      hintKey: 'TUTORIAL.STEPS.ARCHIVES.HINT',
      gifPath: '/assets/tutorial/step_1_gif.gif'
    },
    {
      id: 'interactions',
      route: '/simulations',
      titleKey: 'TUTORIAL.STEPS.INTERACTIONS.TITLE',
      contentKey: 'TUTORIAL.STEPS.INTERACTIONS.CONTENT',
      hintKey: 'TUTORIAL.STEPS.INTERACTIONS.HINT',
      gifPath: '/assets/tutorial/step_2_gif.gif'
    },
    {
      id: 'results',
      route: '/simulations',
      titleKey: 'TUTORIAL.STEPS.RESULTS.TITLE',
      contentKey: 'TUTORIAL.STEPS.RESULTS.CONTENT',
      hintKey: 'TUTORIAL.STEPS.RESULTS.HINT',
      gifPath: '/assets/tutorial/step_3_gif.gif'
    },
    {
      id: 'references',
      route: '/references',
      titleKey: 'TUTORIAL.STEPS.REFERENCES.TITLE',
      contentKey: 'TUTORIAL.STEPS.REFERENCES.CONTENT',
      hintKey: 'TUTORIAL.STEPS.REFERENCES.HINT',
      gifPath: '/assets/tutorial/step_4_gif.gif'
    },
    {
      id: 'models',
      route: '/models',
      titleKey: 'TUTORIAL.STEPS.MODELS.TITLE',
      contentKey: 'TUTORIAL.STEPS.MODELS.CONTENT',
      hintKey: 'TUTORIAL.STEPS.MODELS.HINT',
      gifPath: '/assets/tutorial/step_5_gif.gif'
    },
    {
      id: 'retrievers',
      route: '/retrievers',
      titleKey: 'TUTORIAL.STEPS.RETRIEVERS.TITLE',
      contentKey: 'TUTORIAL.STEPS.RETRIEVERS.CONTENT',
      hintKey: 'TUTORIAL.STEPS.RETRIEVERS.HINT',
      gifPath: '/assets/tutorial/step_6_gif.gif'
    },
    {
      id: 'retriever-results',
      route: '/retrievers',
      titleKey: 'TUTORIAL.STEPS.RETRIEVER_RESULT.TITLE',
      contentKey: 'TUTORIAL.STEPS.RETRIEVER_RESULT.CONTENT',
      hintKey: 'TUTORIAL.STEPS.RETRIEVER_RESULT.HINT',
      gifPath: '/assets/tutorial/step_7_gif.gif'
    },
    {
      id: 'settings',
      route: '/settings/tags',
      titleKey: 'TUTORIAL.STEPS.SETTINGS.TITLE',
      contentKey: 'TUTORIAL.STEPS.SETTINGS.CONTENT',
      hintKey: 'TUTORIAL.STEPS.SETTINGS.HINT',
      gifPath: '/assets/tutorial/step_8_gif.gif'
    }
  ];

  private readonly stateSubject = new BehaviorSubject<TutorialState>({
    isOpen: false,
    currentStepIndex: 0,
    steps: this.steps
  });

  readonly state$ = this.stateSubject.asObservable();

  private currentPreferences: UserPreferences = this.getDefaultPreferences();
  private hasLoadedPreferences = false;

  constructor(
    private usersService: UsersService,
    private interactionsService: InteractionsService,
    private router: Router,
    private tenantService: TenantService
  ) {}

  initAutoStart(): void {
    if (!this.hasSessionToken()) {
      return;
    }

    this.usersService.getMe().subscribe({
      next: (response) => {
        this.currentPreferences = this.normalizePreferences(response.user?.preferences);
        this.hasLoadedPreferences = true;
        if (!this.currentPreferences.tutorial_seen) {
          this.startTutorial();
        }
      }
    });
  }

  startManual(): void {
    this.startTutorial();
  }

  next(): void {
    const state = this.stateSubject.value;
    if (!state.isOpen) {
      return;
    }

    if (state.currentStepIndex >= state.steps.length - 1) {
      this.finish();
      return;
    }

    this.setStep(state.currentStepIndex + 1);
  }

  back(): void {
    const state = this.stateSubject.value;
    if (!state.isOpen || state.currentStepIndex <= 0) {
      return;
    }

    this.setStep(state.currentStepIndex - 1);
  }

  skip(): void {
    this.close();
    this.markTutorialSeen();
    this.navigateToMainPage();
  }

  finish(): void {
    this.close();
    this.markTutorialSeen();
    this.navigateToMainPage();
  }

  private startTutorial(): void {
    this.stateSubject.next({
      isOpen: true,
      currentStepIndex: 0,
      steps: this.steps
    });
    this.navigateForCurrentStep();
  }

  private setStep(index: number): void {
    this.stateSubject.next({
      ...this.stateSubject.value,
      currentStepIndex: index
    });
    this.navigateForCurrentStep();
  }

  private navigateForCurrentStep(): void {
    const step = this.stateSubject.value.steps[this.stateSubject.value.currentStepIndex];
    if (!step) {
      return;
    }

    if (step.id === 'results') {
      this.navigateToResultsStep();
      return;
    }

    this.router.navigate(this.tenantService.buildRoute(step.route));
  }

  private navigateToResultsStep(): void {
    this.interactionsService.list(undefined, undefined, 1, 1).subscribe({
      next: (response) => {
        const firstInteraction = response.interactions?.[0];
        if (firstInteraction?.id) {
          this.router.navigate(this.tenantService.buildRoute(['/simulations', firstInteraction.id]), {
            queryParams: { tutorialTab: 'results' }
          });
          return;
        }

        this.router.navigate(this.tenantService.buildRoute('/simulations'));
      },
      error: () => {
        this.router.navigate(this.tenantService.buildRoute('/simulations'));
      }
    });
  }

  private markTutorialSeen(): void {
    this.ensurePreferencesLoaded(() => {
      if (this.currentPreferences.tutorial_seen) {
        return;
      }

      const updatedPreferences: UserPreferences = {
        ...this.currentPreferences,
        tutorial_seen: true
      };

      this.usersService.updatePreferences(updatedPreferences).subscribe({
        next: (response) => {
          this.currentPreferences = this.normalizePreferences(response.preferences);
          this.hasLoadedPreferences = true;
        }
      });
    });
  }

  private close(): void {
    this.stateSubject.next({
      ...this.stateSubject.value,
      isOpen: false,
      currentStepIndex: 0
    });
  }

  private navigateToMainPage(): void {
    this.router.navigate(this.tenantService.buildRoute('/simulations'));
  }

  private normalizePreferences(preferences?: UserPreferences): UserPreferences {
    return {
      ...this.getDefaultPreferences(),
      ...(preferences || {})
    };
  }

  private getDefaultPreferences(): UserPreferences {
    return { ...DEFAULT_USER_PREFERENCES };
  }

  private hasSessionToken(): boolean {
    return !!sessionStorage.getItem('token');
  }

  private ensurePreferencesLoaded(onLoaded: () => void): void {
    if (this.hasLoadedPreferences) {
      onLoaded();
      return;
    }

    if (!this.hasSessionToken()) {
      return;
    }

    this.usersService.getMe().subscribe({
      next: (response) => {
        this.currentPreferences = this.normalizePreferences(response.user?.preferences);
        this.hasLoadedPreferences = true;
        onLoaded();
      }
    });
  }
}
